import React, { memo, useCallback, useContext, useMemo, useState } from 'react'
import { SuperChartConfiguration_DateRange } from '../../../proto/generated/superchart-page'
import './ChartDurations.css'
import { useDispatch, useSelector } from 'react-redux'
import { Box, ClickAwayListener, Divider, Paper, Tooltip, Typography } from '@mui/material'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { ExtraSmallButton, NonInteractiveTooltip } from '../../custom'
import { selectActiveChartId, selectDateRanges, selectScreenerChartDateRange } from '../../../selectors/chart'
import { AppContext } from '../../../App'
import { updateScreenerSelectedTime } from '../../../reducer/chart.reducer'
import { ShortcutsCategories, ShortcutType } from '../../KeyboardShortcuts/Shortcuts.types'
import { ChartDurationShortcutKeyName } from '../../KeyboardShortcuts/ShortcutsKeymap'
import useShortcuts from '../../../hooks/useShortcuts'
import { selectMultiPaneFullScreen } from '../../../selectors/common'
import { ChartViewKey, MULTIPANE_FULLSCREEN_MODE_ID } from '../../../assets/constants'
import { makeStyles } from 'tss-react/mui'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import { ShortcutTooltipTitle, ShortcutKeys } from '../../KeyboardShortcuts/ShortcutTooltipTitle'

export type DateRangeButtonProps = {
    dateRange: SuperChartConfiguration_DateRange
    selected: boolean
    onClick: (dateRange: SuperChartConfiguration_DateRange) => void
    disable: boolean
}

const useStyles = makeStyles()((theme) => ({
    paperContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flexGrow: 1,
        borderTopRightRadius: 0,
        borderTopLeftRadius: 0,
        borderBottomRightRadius: 0,
        height: 'auto'
    },
    boxContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        height: '100%'
    },
    goToText: {
        whiteSpace: 'nowrap'
    },
    hiddenDatePicker: {
        padding: 0,
        width: 0,
        height: 0,
        overflow: 'hidden'
    },
    hiddenTextField: {
        width: 0,
        height: 0
    },
    dateRangeButton: {
        padding: theme.spacing(0.6, 1)
    }
}))

const DateRangeButton: React.FC<DateRangeButtonProps> = memo(({ dateRange, selected, onClick, disable }) => {
    const { classes } = useStyles()

    const onDateRangeSelected = useCallback(() => {
        onClick(dateRange)
    }, [dateRange, onClick])

    return (
        <NonInteractiveTooltip placement={'top'} title={dateRange.description} key={dateRange.code}>
            <span>
                <ExtraSmallButton
                    variant={selected ? 'contained' : 'text'}
                    color={'inherit'}
                    className={classes.dateRangeButton}
                    onClick={!selected ? onDateRangeSelected : undefined}
                    data-event={'Chart Duration Changed'}
                    data-event-duration={dateRange.name}
                    disabled={disable}
                >
                    <Typography>{dateRange.name}</Typography>
                </ExtraSmallButton>
            </span>
        </NonInteractiveTooltip>
    )
})

const ChartDurations = ({ disable = false, screenerChart = false, multiStrikeTab = false }: { disable?: boolean; screenerChart?: boolean, multiStrikeTab?: boolean }) => {
    const dispatch = useDispatch()
    const activeChartId = useSelector(selectActiveChartId)
    const dateRanges = useSelector(selectDateRanges)
    const selectedDateRange = useSelector(selectScreenerChartDateRange)
    const [dateModalOpen, setDateModalOpen] = useState(false)
    const appContext = useContext(AppContext)
    let chart: any = appContext.getChart(activeChartId)
    const securityMap = chart?.securityMap
    const expiryDate = useMemo(() => {
        if (!securityMap) return null;

        const expiries = Object.values(securityMap)
            .map((sec: any) => sec?.expiry?.expiry) // get timestamp in ms
            .filter((exp): exp is number => typeof exp === 'number');

        if (expiries.length === 0) return null;

        const maxExpiry = Math.max(...expiries);
        return dayjs(maxExpiry);
    }, [securityMap]);

    const { classes } = useStyles()
    const shortcuts: ShortcutType = useMemo(
        () => ({
            [ChartDurationShortcutKeyName.openChartDuration]: () => {
                AnalyticsManager.logEvent('Chart Duration Dialog Opened', { source: 'keyboard' })
                setDateModalOpen(true)
            }
        }),
        []
    )

    useShortcuts(ShortcutsCategories.CHART_DURATION, shortcuts)

    const onDateRangeSelected = useCallback((dateRange: SuperChartConfiguration_DateRange) => {
        if (screenerChart) {
            dispatch(updateScreenerSelectedTime(dateRange))
        } else {
            let chart: any = appContext.getChart(activeChartId)
            if(multiStrikeTab) {
                chart = appContext.getMultiStrikeChart(ChartViewKey.MULTI_STRIKES_CHART)
            }
            if (chart) {
                chart?.setSelectedDateRange(dateRange)
            }
        }
    }, [activeChartId, appContext, dispatch, multiStrikeTab, screenerChart])

    const onDateSelect = useCallback((value: any) => {
        if (!value) return
        const date = value.toDate()
        let chart: any = appContext.getChart(activeChartId)
        if(multiStrikeTab) {
            chart = appContext.getMultiStrikeChart(ChartViewKey.MULTI_STRIKES_CHART)
        }
        if (chart) {
            chart?.goToDate(Math.floor(date.getTime() / 1000), true)
        }
        setDateModalOpen(false)
    }, [activeChartId, appContext, multiStrikeTab])
    const container = useSelector(selectMultiPaneFullScreen)

    return (
        <Paper
            className={classes.paperContainer}
            id={screenerChart ? 'screener-chart-durations' : multiStrikeTab ? 'multi-strike-chart-durations' : 'chart-durations'}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box className={classes.boxContainer}>
                    {dateRanges?.map((dateRange: SuperChartConfiguration_DateRange) => {
                        return (
                            <DateRangeButton
                                key={dateRange.code}
                                dateRange={dateRange}
                                selected={screenerChart && selectedDateRange?.code === dateRange?.code}
                                onClick={onDateRangeSelected}
                                disable={disable}
                            />
                        )
                    })}
                    {!screenerChart && (
                        <>
                            <Divider orientation="vertical" />
                            <Tooltip
                                arrow
                                placement="top"
                                title={
                                    <ShortcutTooltipTitle label="Go to">
                                        <ShortcutKeys keys={['⌥', 'G']} />
                                    </ShortcutTooltipTitle>
                                }
                            >
                                <span>
                                <ExtraSmallButton variant="text" disabled={disable} color={'inherit'} onClick={() => setDateModalOpen(true)} data-event={`Chart Duration Go To Click`}>
                                    <Typography className={classes.goToText}>Go to..</Typography>
                                </ExtraSmallButton>
                                </span>
                            </Tooltip>
                            {dateModalOpen && (
                                <ClickAwayListener onClickAway={() => setDateModalOpen(false)}>
                                    <div>
                                        <DateTimePicker
                                            open={dateModalOpen}
                                            closeOnSelect
                                            ampm={false}
                                            slotProps={{
                                                textField: {
                                                    style: {
                                                        width: 0,
                                                        height: 0
                                                    }
                                                },
                                                popper: {
                                                    container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null
                                                }
                                            }}
                                            disableFuture={true}
                                            className={classes.hiddenDatePicker}
                                            defaultValue={ expiryDate && expiryDate.isBefore(dayjs()) ? expiryDate.hour(9).minute(15) : dayjs().hour(9).minute(15)}
                                            onAccept={onDateSelect}
                                            shouldDisableDate={(date) => expiryDate ? date.isAfter(expiryDate, 'day') : false}
                                        />
                                    </div>
                                </ClickAwayListener>
                            )}
                        </>
                    )}
                </Box>
            </LocalizationProvider>
        </Paper>
    )
}

export default memo(ChartDurations)
